import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

// AB#3280 — the ONE responsive breakpoint. Below this the renderer and the tenant shell switch to
// their compact (phone) layout; at or above it every surface renders exactly what it rendered
// before this ticket existed. One number, exported, so the renderer, the shell, the designer's
// phone preview and the tests can never disagree about where the line is.
export const COMPACT_BREAKPOINT_PX = 768

/**
 * The two layout tiers. Deliberately a two-value vocabulary, not a px-range ladder: every compact
 * decision in the renderer is "collapse or don't", and a tablet middle tier would double the test
 * matrix for no current consumer (CRM-061 targets phones).
 */
export type ViewportTier = 'compact' | 'wide'

// Default 'wide' — SSR, tests, and any host that never mounts a ViewportProvider all render the
// desktop layout byte-for-byte. Compact is strictly opt-in via the provider (or a forced prop).
const ViewportContext = createContext<ViewportTier>('wide')

function mediaTier(): ViewportTier {
    // SSR / non-browser guard: no window (or no matchMedia, e.g. bare jsdom) = desktop.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') { return 'wide' }
    return window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT_PX - 1}px)`).matches ? 'compact' : 'wide'
}

/**
 * AB#3280 — the current layout tier. Components read this instead of window.innerWidth so a test
 * (or the designer's phone preview) can force a tier by mounting a ViewportProvider, and so a
 * component rendered with no provider at all stays on the desktop path.
 */
export function useViewport(): ViewportTier {
    return useContext(ViewportContext)
}

/**
 * Tracks the real viewport via matchMedia and re-renders consumers when the 768px line is crossed.
 * `force` pins the tier regardless of the real viewport — the designer phone preview and component
 * tests use it; production hosts omit it.
 */
export function ViewportProvider({ children, force }: { children: ReactNode; force?: ViewportTier | undefined }) {
    const [tier, setTier] = useState<ViewportTier>(mediaTier)
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') { return }
        const mql = window.matchMedia(`(max-width: ${COMPACT_BREAKPOINT_PX - 1}px)`)
        const onChange = () => setTier(mql.matches ? 'compact' : 'wide')
        onChange()
        mql.addEventListener('change', onChange)
        return () => mql.removeEventListener('change', onChange)
    }, [])
    return <ViewportContext.Provider value={force ?? tier}>{children}</ViewportContext.Provider>
}
